import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { FileXlsIcon, PrinterIcon } from "@phosphor-icons/react";
import * as XLSX from "xlsx";
import { getOilStatus } from "@/components/TrafficLight";
import { printFleetReport, printTripsReport } from "@/lib/printUtils";

export const ReportsPage = () => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [cars, setCars] = useState([]);
  const [trips, setTrips] = useState([]);

  useEffect(() => {
    (async () => {
      const [c, tr] = await Promise.all([api.get("/cars"), api.get("/trips")]);
      setCars(c.data);
      setTrips(tr.data);
    })();
  }, []);

  const officeHeader = () => {
    const rows = [];
    if (user?.office_name) rows.push([user.office_name]);
    rows.push([new Date().toLocaleString(lang === "ar" ? "ar-EG" : "en-US")]);
    rows.push([]);
    return rows;
  };

  const exportCarsExcel = () => {
    const rows = cars.map((c) => {
      const st = getOilStatus(c);
      return {
        [t("plateNumber")]: c.plate_number,
        [t("make")]: c.make,
        [t("model")]: c.model,
        [t("year")]: c.year || "",
        [t("currentKm")]: c.current_km,
        [t("lastOilKm")]: c.last_oil_change_km,
        [t("intervalKm")]: c.oil_change_interval_km,
        [t("remainingKm")]: st.remaining,
        [t("status")]: st.level,
      };
    });
    const ws = XLSX.utils.aoa_to_sheet(officeHeader());
    XLSX.utils.sheet_add_json(ws, rows, { origin: -1 });
    if (!ws["!cols"]) ws["!cols"] = new Array(9).fill({ wch: 16 });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cars");
    XLSX.writeFile(wb, "fleet-cars.xlsx");
  };

  const exportTripsExcel = () => {
    const carMap = Object.fromEntries(cars.map((c) => [c.car_id, c]));
    const rows = trips.map((tr) => {
      const c = carMap[tr.car_id];
      return {
        [t("date")]: new Date(tr.return_date).toLocaleString(lang === "ar" ? "ar-EG" : "en-US"),
        [t("plateNumber")]: c?.plate_number || "",
        [t("make")]: c?.make || "",
        [t("model")]: c?.model || "",
        [t("renterName")]: tr.renter_name || "",
        [t("returnKm")]: tr.return_km,
        [t("kmDriven")]: tr.km_driven,
        [t("notes")]: tr.notes || "",
      };
    });
    const ws = XLSX.utils.aoa_to_sheet(officeHeader());
    XLSX.utils.sheet_add_json(ws, rows, { origin: -1 });
    if (!ws["!cols"]) ws["!cols"] = new Array(8).fill({ wch: 16 });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Returns");
    XLSX.writeFile(wb, "fleet-returns.xlsx");
  };

  const printCars = () => printFleetReport({ cars, user, lang });
  const printTrips = () => printTripsReport({ trips, cars, user, lang });

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">/exports</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t("reports")}</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ReportCard
          title={t("fleetOverview")}
          desc={`${cars.length} ${t("cars").toLowerCase()}`}
          onExcel={exportCarsExcel}
          onPdf={printCars}
          t={t}
          testKey="cars"
        />
        <ReportCard
          title={t("tripHistory")}
          desc={`${trips.length} ${t("trips").toLowerCase()}`}
          onExcel={exportTripsExcel}
          onPdf={printTrips}
          t={t}
          testKey="trips"
        />
      </div>
    </div>
  );
};

const ReportCard = ({ title, desc, onExcel, onPdf, t, testKey }) => (
  <div className="border border-white/10 bg-[#121212] p-6 card-hover">
    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{desc}</div>
    <div className="text-2xl font-bold mt-1 mb-6">{title}</div>
    <div className="flex gap-2 flex-wrap">
      <button
        data-testid={`export-excel-${testKey}`}
        onClick={onExcel}
        className="inline-flex items-center gap-2 border border-white/10 bg-[#0A0A0A] hover:bg-[#1C1C1C] px-4 py-2 text-sm transition-colors"
      >
        <FileXlsIcon size={16} weight="bold" className="text-[#39FF14]" /> {t("exportExcel")}
      </button>
      <button
        data-testid={`export-pdf-${testKey}`}
        onClick={onPdf}
        className="inline-flex items-center gap-2 border border-white/10 bg-[#0A0A0A] hover:bg-[#1C1C1C] px-4 py-2 text-sm transition-colors"
      >
        <PrinterIcon size={16} weight="bold" /> {t("exportPdf")}
      </button>
    </div>
  </div>
);
